import { forwardRef, type CSSProperties, type InputHTMLAttributes, type ReactNode, useId } from "react"
import classNames from "clsx"

type BaseInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "children" | "style"> & {
  label: ReactNode
  id?: string
  hint?: ReactNode
  error?: ReactNode
  layout?: "stack" | "inline"
  assistiveText?: ReactNode
  inputStyle?: CSSProperties
  style?: CSSProperties
}

export type TextInputProps = BaseInputProps

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  ({ label, id, hint, error, layout = "stack", assistiveText, className, style, inputStyle, ...inputProps }, ref) => {
    const generatedId = useId()
    const inputId = id ?? generatedId
    const hintId = hint ? `${inputId}-hint` : undefined
    const errorId = error ? `${inputId}-error` : undefined
    const assistiveId = assistiveText ? `${inputId}-assistive` : undefined
    const describedBy = [errorId, hintId, assistiveId].filter(Boolean).join(" ") || undefined

    return (
      <div
        style={{
          display: "flex",
          flexDirection: layout === "inline" ? "row" : "column",
          alignItems: layout === "inline" ? "center" : "stretch",
          gap: layout === "inline" ? "16px" : "12px",
          width: "100%",
          ...style,
        }}
      >
        <label
          htmlFor={inputId}
          className="mono small"
          style={{
            flex: layout === "inline" ? "0 0 auto" : undefined,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            display: "flex",
            alignItems: layout === "inline" ? "center" : "flex-start",
            gap: layout === "inline" ? "12px" : "8px",
            color: "var(--muted)",
          }}
        >
          <span style={{ color: "var(--text)", letterSpacing: "0.14em" }}>{label}</span>
          {hint ? (
            <span id={hintId} className="tiny" style={{ letterSpacing: "0.08em" }}>
              {hint}
            </span>
          ) : null}
        </label>
        <div style={{ flex: "1 1 auto", display: "flex", flexDirection: "column", gap: "8px" }}>
          <input
            {...inputProps}
            ref={ref}
            id={inputId}
            className={classNames("input-mono", className)}
            style={{
              width: "100%",
              minWidth: 0,
              height: "58px",
              padding: "10px 14px",
              border: "1px solid var(--rail)",
              background: "transparent",
              color: "var(--text)",
              fontSize: "26px",
              lineHeight: 1,
              ...inputStyle,
            }}
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={describedBy}
          />
          {assistiveText ? (
            <div id={assistiveId} className="tiny mono" style={{ color: "var(--muted)", letterSpacing: "0.08em" }}>
              {assistiveText}
            </div>
          ) : null}
          {error ? (
            <div id={errorId} className="tiny mono" style={{ color: "var(--error-text)", letterSpacing: "0.08em" }}>
              {error}
            </div>
          ) : null}
        </div>
      </div>
    )
  },
)

TextInput.displayName = "TextInput"

export default TextInput
